import { useEffect, useState } from "react";
import { Mail, Phone, MessageSquare, Check, Trash2, Inbox, Eye } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const STATUS_LABELS = {
  new: { label: "Nuevo", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  read: { label: "Leído", color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  answered: { label: "Respondido", color: "bg-sky-500/20 text-sky-300 border-sky-500/30" },
  archived: { label: "Archivado", color: "bg-white/10 text-slate-400 border-white/10" },
};

export default function AdminContacts() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/support/contacts${filter ? `?status=${filter}` : ""}`);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/support/contacts/${id}`, { status });
      toast.success("Estado actualizado");
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    try {
      await api.delete(`/admin/support/contacts/${id}`);
      toast.success("Mensaje eliminado");
      if (selected?.id === id) setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-contacts">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-white tracking-tight">Mensajes de contacto</h1>
          <p className="text-sm text-slate-400 mt-1">{messages.length} mensaje{messages.length !== 1 ? "s" : ""}{filter ? ` · filtro: ${STATUS_LABELS[filter]?.label || filter}` : ""}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[["", "Todos"], ["new", "Nuevos"], ["read", "Leídos"], ["answered", "Respondidos"], ["archived", "Archivados"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`text-xs px-3 py-1.5 rounded-xl border transition-colors ${
                filter === v ? "bg-gcgold text-ink border-gcgold font-bold" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
              }`}
              data-testid={`filter-${v || "all"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* List */}
        <div className="lg:col-span-2 bg-ink-800 border border-white/5 rounded-2xl overflow-hidden flex flex-col max-h-[70vh]">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 font-mono">
            <Inbox className="h-3.5 w-3.5" /> Bandeja
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-white/5">
            {loading && <div className="p-6 text-center text-sm text-slate-500">Cargando...</div>}
            {!loading && messages.length === 0 && (
              <div className="p-10 text-center">
                <Inbox className="h-10 w-10 text-slate-700 mx-auto" />
                <p className="text-sm text-slate-500 mt-3">No hay mensajes</p>
              </div>
            )}
            {messages.map((m) => {
              const isSel = selected?.id === m.id;
              const status = STATUS_LABELS[m.status] || STATUS_LABELS.new;
              return (
                <button
                  key={m.id}
                  onClick={() => setSelected(m)}
                  className={`w-full text-left p-4 transition-colors ${isSel ? "bg-gcgold/10 border-l-2 border-l-gcgold" : "hover:bg-white/[0.03]"}`}
                  data-testid={`contact-item-${m.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-bold text-white text-sm truncate">{m.name}</span>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 truncate">{m.email}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{m.message}</div>
                  <div className="text-[10px] text-slate-600 font-mono mt-1.5">
                    {new Date(m.created_at).toLocaleString("es-ES")}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 bg-ink-800 border border-white/5 rounded-2xl p-6">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
              <Eye className="h-10 w-10 text-slate-700" />
              <p className="text-sm text-slate-500 mt-3">Selecciona un mensaje para verlo</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h2 className="font-display text-xl font-bold text-white">{selected.name}</h2>
                  <div className="text-xs font-mono text-slate-500 mt-1">
                    {new Date(selected.created_at).toLocaleString("es-ES")}
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border self-start ${STATUS_LABELS[selected.status]?.color || STATUS_LABELS.new.color}`}>
                  {STATUS_LABELS[selected.status]?.label || "Nuevo"}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] transition-colors">
                  <Mail className="h-4 w-4 text-gcgold" />
                  <span className="text-sm text-slate-200 truncate">{selected.email}</span>
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] transition-colors">
                    <Phone className="h-4 w-4 text-gcgold" />
                    <span className="text-sm text-slate-200">{selected.phone}</span>
                  </a>
                )}
              </div>

              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 font-mono mb-3">
                  <MessageSquare className="h-3 w-3" /> Mensaje
                </div>
                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
                {selected.status !== "read" && (
                  <Button size="sm" onClick={() => updateStatus(selected.id, "read")} className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30 rounded-xl">
                    Marcar leído
                  </Button>
                )}
                {selected.status !== "answered" && (
                  <Button size="sm" onClick={() => updateStatus(selected.id, "answered")} className="bg-sky-500/20 text-sky-300 hover:bg-sky-500/30 border border-sky-500/30 rounded-xl">
                    <Check className="h-3.5 w-3.5 mr-1.5" /> Marcar respondido
                  </Button>
                )}
                {selected.status !== "archived" && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(selected.id, "archived")} className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 rounded-xl">
                    Archivar
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => remove(selected.id)} className="bg-rose-500/10 border-rose-500/30 text-rose-300 hover:bg-rose-500/20 ml-auto rounded-xl">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eliminar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
